import { FC, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/modules/api";
import { getFileDownloadUrl } from "@/pages/utilities/uploaded-files/utils";
import { useAtlasAssetToken } from "@/contexts/atlas-asset-token";
import { buildProxiedAssetUrl } from "@/modules/asset-proxy";

interface UploadedImageProps {
  uploadedFileId: string | null | undefined;
  size?: number;
}

export const UploadedImage: FC<UploadedImageProps> = ({ uploadedFileId, size = 32 }) => {
  const { token, ensureToken } = useAtlasAssetToken();

  const result = useQuery({
    queryKey: ["uploaded-file", uploadedFileId],
    queryFn: () =>
      api.get("/internal/v1/uploaded-files/{uploaded_file_id}", {
        params: { path: { uploaded_file_id: uploadedFileId! } },
      }),
    enabled: !!uploadedFileId,
  });

  // Ensure we have an asset token when we have a file to display
  useEffect(() => {
    if (result.data?.data && !token) {
      void ensureToken();
    }
  }, [result.data?.data, token, ensureToken]);

  if (!uploadedFileId || !result.data?.data) return null;

  const file = result.data.data;
  const originalUrl = getFileDownloadUrl(
    file.uploaded_file_bucket_name,
    file.uploaded_file_blob_name
  );

  // Use proxied URL if we have a token, otherwise fall back to original
  const url = token ? buildProxiedAssetUrl({ originalUrl, token }) : originalUrl;

  return (
    <img
      src={url}
      alt="uploaded"
      style={{ width: size, height: size, objectFit: "cover", borderRadius: 4 }}
    />
  );
};
